import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, Button, Stack, Chip, IconButton, CircularProgress, Checkbox } from '@mui/joy';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetPendingWriteOffRequests, APIReviewWriteOffRequest,
    APIBulkReviewWriteOffRequests, parseApiError,
} from '../../api';

const fmtQty = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v;
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
};
const fmtDateTime = (iso) => {
    try {
        return new Date(iso).toLocaleString('ru-RU', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        });
    } catch { return ''; }
};

export default function WriteOffRequestsPage() {
    const { token, showToast, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await APIGetPendingWriteOffRequests(token);
        setList(Array.isArray(res) ? res : []);
        setSelected(new Set());          // сбрасываем выбор при перезагрузке
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);
    // Кнопка «обновить» в шапке — подписываемся на глобальный тик обновления.
    useEffect(() => onRefreshTick(load), [onRefreshTick, load]);

    const toggleOne = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const allSelected = list.length > 0 && selected.size === list.length;
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(list.map(r => r.id)));

    // Одиночное рассмотрение
    const review = async (req, action) => {
        let comment = '';
        if (action === 'reject') comment = window.prompt('Причина отклонения (необязательно):') || '';
        setBusyId(req.id);
        const res = await APIReviewWriteOffRequest(token, req.id, { action, comment });
        setBusyId(null);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось'), 'danger'); return; }
        showToast(action === 'approve' ? 'Списание одобрено ✓' : 'Заявка отклонена',
            action === 'approve' ? 'success' : 'warning');
        setSelected(prev => { const n = new Set(prev); n.delete(req.id); return n; });
        setList(prev => prev.filter(r => r.id !== req.id));
    };

    // Массовое рассмотрение выбранных
    const bulkReview = async (action) => {
        const ids = [...selected];
        if (ids.length === 0) return;
        let comment = '';
        if (action === 'reject') comment = window.prompt(`Причина отклонения (${ids.length}) заявок (необязательно):`) || '';
        else if (!window.confirm(`Одобрить и списать выбранные заявки (${ids.length})?`)) return;

        setBulkBusy(true);
        const res = await APIBulkReviewWriteOffRequests(token, { ids, action, comment });
        setBulkBusy(false);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось'), 'danger'); return; }

        const done = new Set(res.processed || ids);
        showToast(
            action === 'approve' ? `Одобрено списаний: ${done.size} ✓` : `Отклонено заявок: ${done.size}`,
            action === 'approve' ? 'success' : 'warning',
        );
        setList(prev => prev.filter(r => !done.has(r.id)));
        setSelected(new Set());
    };

    return (
        <>
            <Header />
            <Box sx={{ maxWidth: 720, mx: 'auto', px: 2, py: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <IconButton variant="plain" onClick={() => navigate('/manager')}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Inventory2RoundedIcon sx={{ color: 'var(--joy-palette-primary-400)' }} />
                    <Typography level="h3" sx={{ fontWeight: 800, flex: 1 }}>Заявки на списание</Typography>
                    <Chip variant="soft" color="warning" sx={{ fontWeight: 800 }}>{list.length}</Chip>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                ) : list.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', color: 'var(--joy-palette-text-tertiary)', py: 8 }}>
                        Нет заявок на согласование
                    </Typography>
                ) : (
                    <>
                        {/* Панель массовых действий */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap',
                            px: 1.5, py: 1, borderRadius: 'var(--joy-radius-lg)',
                            bgcolor: 'var(--joy-palette-background-level1)',
                            border: '1px solid var(--joy-palette-divider)',
                        }}>
                            <Checkbox
                                size="sm" label="Выбрать все"
                                checked={allSelected}
                                indeterminate={selected.size > 0 && !allSelected}
                                onChange={toggleAll}
                                sx={{ fontWeight: 700 }}
                            />
                            <Box sx={{ flex: 1 }} />
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                Выбрано: {selected.size}
                            </Typography>
                            <Button size="sm" variant="soft" color="danger"
                                disabled={selected.size === 0 || bulkBusy} loading={bulkBusy}
                                onClick={() => bulkReview('reject')} sx={{ fontWeight: 700 }}>
                                Отклонить
                            </Button>
                            <Button size="sm" variant="solid" color="success"
                                disabled={selected.size === 0 || bulkBusy} loading={bulkBusy}
                                onClick={() => bulkReview('approve')} sx={{ fontWeight: 800 }}>
                                Одобрить ({selected.size})
                            </Button>
                        </Box>

                        <Stack spacing={1.2}>
                            {list.map(req => (
                                <Box key={req.id} sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    borderRadius: 'var(--joy-radius-xl)', p: 2,
                                    outline: selected.has(req.id) ? '2px solid var(--joy-palette-primary-400)' : 'none',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                        <Checkbox size="sm" checked={selected.has(req.id)}
                                            onChange={() => toggleOne(req.id)} sx={{ mr: 0.5 }} />
                                        <Typography sx={{ fontWeight: 800, flex: 1 }}>{req.comment}</Typography>
                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                            {fmtDateTime(req.created_at)}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 1 }}>
                                        {req.acting_admin_name || req.requested_by_name || '—'}{req.location_name ? ` · ${req.location_name}` : ''}
                                    </Typography>
                                    <Stack spacing={0.4} sx={{ mb: 1.5 }}>
                                        {(req.items || []).map(it => (
                                            <Box key={it.id} sx={{
                                                display: 'flex', justifyContent: 'space-between',
                                                px: 1.2, py: 0.6, borderRadius: 'var(--joy-radius-md)',
                                                bgcolor: 'var(--joy-palette-background-level2)',
                                            }}>
                                                <Typography sx={{ fontSize: 13 }}>{it.product_name_snapshot}</Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'var(--joy-palette-danger-400)' }}>
                                                    −{fmtQty(it.quantity)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                    <Stack direction="row" spacing={1}>
                                        <Button fullWidth variant="soft" color="danger" loading={busyId === req.id}
                                            onClick={() => review(req, 'reject')} sx={{ fontWeight: 700 }}>
                                            Отклонить
                                        </Button>
                                        <Button fullWidth variant="solid" color="success" loading={busyId === req.id}
                                            onClick={() => review(req, 'approve')} sx={{ fontWeight: 800 }}>
                                            Одобрить и списать
                                        </Button>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    </>
                )}
            </Box>
        </>
    );
}